import { useTranslation } from "react-i18next";
import CRUDTable from "../CRUDTable";
import React from "react";
import { Chip } from "@mui/material";
import { getFilials } from "../../../redux/actions/filialActions";
import { accreditationIndicatorCRUD, filialDivisionCRUD } from "../../../http";
import { exportAccreditationIndicatorExcel } from "../../../screens/AccreditationMarkIndicator/accreditationIndicatorExcel";

const formatDate = (value) => {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("ru-RU");
};

const calcAccreditationStatus = (record) => {
    if (record?.isIrrelevant) return { label: "Неактуален", color: "default" };
    const validity = record?.validityPeriod ? new Date(record.validityPeriod) : null;
    if (!validity || isNaN(validity.getTime())) return { label: "Не задано", color: "default" };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    validity.setHours(0, 0, 0, 0);

    // Если дата уже прошла
    if (validity < today) {
        return { label: "Просрочен", color: "error" };
    }

    // Вычисляем разницу в днях
    const diffTime = validity - today;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Пороги в днях (используем среднее количество дней в месяце: 30.44)
    const days9Months = 9 * 30.44; // ~274 дня
    const days8Months = 8 * 30.44;  // ~244 дня
    const days6Months = 6 * 30.44;  // ~183 дня

    // За 9 месяцев до окончания действия аттестата (>= 9 месяцев, т.е. >= ~274 дней) - зеленый индикатор
    if (diffDays >= days9Months) {
        return { label: "Норма", color: "success" };
    }
    // За 8 месяцев до окончания (>= 8 и < 9 месяцев, т.е. >= ~244 и < ~274 дней) - желтый индикатор
    else if (diffDays >= days8Months) {
        return { label: "Внимание", color: "warning" };
    }
    // За 6 месяцев до окончания (>= 6 и < 8 месяцев, т.е. >= ~183 и < ~244 дней) - красный индикатор
    else if (diffDays >= days6Months) {
        return { label: "Срочно", color: "error" };
    }
    // Меньше 6 месяцев до окончания (< ~183 дней) - красный индикатор
    else {
        return { label: "Срочно", color: "error" };
    }
};

export default function AccreditationMarkIndicatorCRUDTable({ extraQuery: extraQueryProp, ...props }) {
    const { t } = useTranslation();

    const formatAddress = (address) => {
        if (!address) return "";
        if (Array.isArray(address)) return address.filter(Boolean).join(", ");
        if (typeof address === "string") {
            const trimmed = address.trim();
            if (!trimmed) return "";
            if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
                try {
                    return formatAddress(JSON.parse(trimmed));
                } catch (e) {
                    // fall through
                }
            }
            return address;
        }
        if (typeof address === "object") {
            const entries = Object.entries(address);
            if (!entries.length) return "";
            return entries
                .map(([k, v]) => {
                    if (v == null) return null;
                    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
                        return `${k}: ${String(v)}`;
                    }
                    try {
                        return `${k}: ${JSON.stringify(v)}`;
                    } catch (e) {
                        return `${k}: ${String(v)}`;
                    }
                })
                .filter(Boolean)
                .join(", ");
        }
        try {
            return JSON.stringify(address);
        } catch (e) {
            return String(address);
        }
    };

    const columns = [
        {
            title: "Номер аттестата аккредитации",
            dataIndex: "certificateNumber",
            key: "certificateNumber",
        },
        {
            title: "Название",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Дата выдачи аттестата аккредитации",
            dataIndex: "issueDate",
            key: "issueDate",
            render: (value) => formatDate(value),
        },
        {
            title: "Срок действия аккредитации",
            dataIndex: "validityPeriod",
            key: "validityPeriod",
            render: (value) => formatDate(value),
        },
        {
            title: "Статус",
            dataIndex: "validityPeriod",
            key: "status",
            render: (_, record) => {
                const s = calcAccreditationStatus(record);
                return <Chip label={s.label} color={s.color} size="small" />;
            },
        },
        {
            title: "Филиал",
            dataIndex: "filial",
            key: "filial",
            render: (value) => value?.name || "",
        },
        {
            title: "Районное отделение",
            dataIndex: "filialDivision",
            key: "filialDivision",
            render: (value) => value?.name || "",
        },
        {
            title: "Адрес",
            dataIndex: "address",
            key: "address",
            render: (value) => formatAddress(value),
        },
    ];

    const searchFields = [
        {
            title: "Номер аттестата",
            key: "certificateNumber",
            compareType: "like",
            operandPosition: 1,
            filterType: "normal",
        },
        {
            title: "Название",
            key: "name",
            compareType: "like",
            operandPosition: 1,
            filterType: "normal",
        },
        {
            title: "Адрес",
            key: "address",
            compareType: "contains",
            operandPosition: 1,
            filterType: "normal",
        },
    ];

    const filterFields = [
        {
            title: "Филиал",
            key: "filialId",
            compareType: "equals",
            reducerKey: "filial",
            reducerArrayKey: "filials",
            reducerAction: getFilials,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        },
        {
            title: "Районное отделение",
            key: "filialDivisionId",
            compareType: "equals",
            reducerKey: "filialDivision",
            operandPosition: 1,
            parentKey: "filialId",
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
            useDomainSelect2: true,
            domainSelectCrud: filialDivisionCRUD,
            domainSelect2SkipFetchWithoutParent: true,
            domainSelect2NoParentHint: "Выберите филиал",
            selectMembers: ["Id", "Name", "FilialId"],
            normalizeOption: (row) => ({
                ...row,
                id: row.id ?? row.Id,
                name: row.name ?? row.Name,
                filialId: row.filialId ?? row.FilialId,
            }),
        },
        {
            title: "Дата выдачи",
            key: "issueDate",
            compareType: "equals",
            operandPosition: 1,
            type: "date",
            filterType: "normal",
        },
        {
            title: "Срок действия до",
            key: "validityPeriod",
            compareType: "lessThanOrEqual",
            operandPosition: 1,
            type: "date",
            filterType: "normal",
        },
        {
            title: "Неактуальный",
            key: "isIrrelevant",
            compareType: "equals",
            operandPosition: 1,
            type: "normal",
            filterType: "normal",
            options: [
                { id: false, name: "Нет" },
                { id: true, name: "Да" },
            ],
        },
    ];

    const rowClassNameHandler = (record) => {
        const s = calcAccreditationStatus(record);
        switch (s.label) {
            case "Норма":
                return "accreditation-indicator-normal"; // Зеленый фон
            case "Внимание":
                return "accreditation-indicator-warning"; // Желтый фон
            case "Срочно":
                return "accreditation-indicator-urgent"; // Красный фон
            case "Просрочен":
                return "accreditation-indicator-expired"; // Красный фон
            case "Неактуален":
                return "accreditation-indicator-irrelevant"; // Серый фон
            default:
                return "";
        }
    };

    const mergedExtraQuery = {
        ...(extraQueryProp || {}),
        includes: [
            ...new Set([
                "FilialDivision",
                ...((extraQueryProp && extraQueryProp.includes) || []),
            ]),
        ],
    };

    return (
        <CRUDTable
            search={accreditationIndicatorCRUD.search}
            extraQuery={mergedExtraQuery}
            columns={columns}
            searchFields={searchFields}
            filterFields={filterFields}
            rowClassNameHandler={rowClassNameHandler}
            onExportExcel={async ({ filter, sort, fileName }) => {
                const data = await accreditationIndicatorCRUD.search({
                    paging: { skip: 0, take: null, returnCount: true },
                    filter,
                    sort,
                });
                exportAccreditationIndicatorExcel(data?.result || [], { fileName });
            }}
            exportExcelFileName={"Индикатор_аккредитации.xlsx"}
            {...props}
        />
    );
}

